import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt, money, reasonText } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { UsageMeter } from "@/components/UsageMeter";

const Stat = ({ label, value, testid, tone = "text-slate-900" }) => (
  <div className="border-l border-slate-200 pl-4 first:border-l-0 first:pl-0">
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone}`} data-testid={testid}>{value}</div>
  </div>
);

const Progress = ({ stats, testid }) => {
  const pct = stats.pages ? Math.round((stats.reviewed * 100) / stats.pages) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs font-mono text-slate-700 mb-1.5">
        <span>{stats.reviewed} / {stats.pages} pages</span><span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-slate-900 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      {stats.needs_check > 0 && <div className="text-xs text-blue-700 mt-1.5">{stats.needs_check} sentence{stats.needs_check > 1 ? "s" : ""} to check</div>}
    </div>
  );
};

const SiteLine = ({ site, onOpen }) => (
  <button type="button" onClick={onOpen} data-testid={`agency-site-${site.id}`} className="w-full text-left grid grid-cols-1 md:grid-cols-[1.3fr_1.2fr_1.2fr_auto] gap-4 md:gap-8 items-center px-5 py-4 border-t border-slate-100 hover:bg-slate-50 transition-colors duration-150 group">
    <div className="min-w-0">
      <div className="font-medium text-sm truncate">{site.name}</div>
      <div className="font-mono text-xs text-slate-500 truncate">{site.domain} · {site.platform === "ghl" ? "GHL es." : "Emergent /es/"}</div>
    </div>
    <UsageMeter row={site.usage} testid={`agency-usage-${site.id}`} />
    <Progress stats={site.stats} testid={`agency-review-${site.id}`} />
    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 hidden md:block" />
  </button>
);

const ClientCard = ({ client, onOpenSite, i }) => (
  <section className="bg-white border border-slate-200 rounded-lg overflow-hidden rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`agency-client-${client.org_id}`}>
    <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-5 py-4 bg-slate-50">
      <div>
        <h2 className="font-display font-semibold text-base md:text-lg">{client.name}</h2>
        <div className="text-xs text-slate-500 font-mono">{client.sites.length} / {client.max_websites ?? "–"} websites</div>
      </div>
      <div className="flex items-center gap-3 text-xs">
        {(client.suspended || !client.access) && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200" data-testid={`agency-client-suspended-${client.org_id}`}>
            <ShieldAlert className="h-3.5 w-3.5" /> {reasonText(client.reason || "no_active_plan")}
          </span>
        )}
        {client.extra_cents > 0 && <span className="font-mono text-red-700" data-testid={`agency-client-extra-${client.org_id}`}>Extra {money(client.extra_cents)}</span>}
      </div>
    </header>
    {client.sites.length === 0 ? (
      <div className="px-5 py-4 text-sm text-slate-500 border-t border-slate-100">No websites yet.</div>
    ) : client.sites.map((s) => <SiteLine key={s.id} site={s} onOpen={() => onOpenSite(client.org_id, s.id)} />)}
  </section>
);

export default function AgencyOverview() {
  const { session, switchOrg } = useSession();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/agency/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const openSite = async (orgId, siteId) => {
    try {
      if (orgId !== session.org_id) await switchOrg(orgId);
      navigate(`/sites/${siteId}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const t = data?.totals;
  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Agency</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">All clients</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-xl">Every client's websites, word usage and review progress on one screen. Click a website to open it.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="agency-totals">
        <Stat label="Clients" value={t?.clients ?? "–"} testid="agency-total-clients" />
        <Stat label="Websites" value={t?.websites ?? "–"} testid="agency-total-websites" />
        <Stat label="Words this month" value={t ? fmt(t.words) : "–"} testid="agency-total-words" />
        <Stat label="Pages reviewed" value={t ? `${t.reviewed} / ${t.pages}` : "–"} testid="agency-total-reviewed" />
        <Stat label="Extra usage" value={money(t?.extra_cents)} testid="agency-total-extra" tone={t?.extra_cents ? "text-red-600" : "text-slate-900"} />
      </div>
      {!data ? (
        <div className="font-mono text-sm text-slate-500" data-testid="agency-loading">Loading clients…</div>
      ) : (
        <div className="space-y-4" data-testid="agency-client-list">
          {data.clients.map((c, i) => <ClientCard key={c.org_id} client={c} onOpenSite={openSite} i={i} />)}
        </div>
      )}
    </div>
  );
}
